import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface FieldVisibilityState {
  enabledFieldIds: Set<string>;
  customPresets: Record<string, string[]>;
  setEnabledFields: (fieldIds: string[]) => void;
  enableField: (fieldId: string) => void;
  disableField: (fieldId: string) => void;
  isFieldEnabled: (fieldId: string) => boolean;
  resetToEssentials: (useCaseId?: string) => void;
  savePreset: (name: string, fieldIds: string[]) => void;
  loadPreset: (name: string) => void;
}

const ESSENTIAL_FIELDS_BY_USE_CASE: Record<string, string[]> = {
  'medical-genetic': [
    'name',
    'gender',
    'birthDate',
    'deathDate',
    'medicalConditionTags',
    'medications',
    'geneticMarkers',
    'notes',
  ],
  'family-therapy': [
    'name',
    'gender',
    'birthDate',
    'emotionalCloseness',
    'conflictLevel',
    'primaryCaregiver',
    'notes',
  ],
  'legal-estate': [
    'name',
    'gender',
    'birthDate',
    'deathDate',
    'maritalStatus',
    'occupation',
    'willStatus',
    'trusteeStatus',
    'notes',
  ],
  'social-work': [
    'name',
    'gender',
    'birthDate',
    'livingArrangement',
    'employmentStatus',
    'incomeLevel',
    'housingStatus',
    'socialSupport',
    'notes',
  ],
  'general-research': [
    'name',
    'gender',
    'birthDate',
    'deathDate',
    'participantId',
    'consentStatus',
    'culturalHeritageTags',
    'notes',
  ],
  'default': [
    'name',
    'gender',
    'birthDate',
    'deathDate',
    'occupation',
    'notes',
  ],
};

function getEssentialFields(useCaseId?: string): string[] {
  return ESSENTIAL_FIELDS_BY_USE_CASE[useCaseId || 'default'] || ESSENTIAL_FIELDS_BY_USE_CASE['default'];
}

export const useFieldVisibilityStore = create<FieldVisibilityState>()(
  persist(
    (set, get) => ({
      enabledFieldIds: new Set(getEssentialFields('default')),
      customPresets: {},

      setEnabledFields: (fieldIds: string[]) => {
        set({ enabledFieldIds: new Set(fieldIds) });
      },

      enableField: (fieldId: string) => {
        const current = get().enabledFieldIds;
        const updated = new Set(current);
        updated.add(fieldId);
        set({ enabledFieldIds: updated });
      },

      disableField: (fieldId: string) => {
        const current = get().enabledFieldIds;
        const updated = new Set(current);
        updated.delete(fieldId);
        set({ enabledFieldIds: updated });
      },

      isFieldEnabled: (fieldId: string) => {
        return get().enabledFieldIds.has(fieldId);
      },

      resetToEssentials: (useCaseId?: string) => {
        const essentials = getEssentialFields(useCaseId);
        set({ enabledFieldIds: new Set(essentials) });
      },

      savePreset: (name: string, fieldIds: string[]) => {
        set((state) => ({
          customPresets: {
            ...state.customPresets,
            [name]: fieldIds,
          },
        }));
      },

      loadPreset: (name: string) => {
        const preset = get().customPresets[name];
        if (preset) {
          set({ enabledFieldIds: new Set(preset) });
        }
      },
    }),
    {
      name: 'field-visibility-storage',
      partialize: (state) => ({
        enabledFieldIds: Array.from(state.enabledFieldIds),
        customPresets: state.customPresets,
      }),
      merge: (persistedState: any, currentState) => ({
        ...currentState,
        enabledFieldIds: new Set(persistedState.enabledFieldIds || []),
        customPresets: persistedState.customPresets || {},
      }),
    }
  )
);
